import {
  ReturnsAndClaimsPermissions,
  UserPermissions,
} from "@redotech/account-common/permissions";
import { isRedoEmployeeEmail } from "@redotech/account-common/redo-employee";
import { ManagedUserRoles } from "@redotech/account-common/roles";
import { TeamType as AccountTeamType } from "@redotech/account-common/team-type";
import { GetUser } from "@redotech/account-common/user";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import { SupportAiBillingVersion } from "@redotech/redo-billing-common/support-ai-billing-version";
import { SupportBaseBillingVersion } from "@redotech/redo-billing-common/team-billing";
import { isUnitedStatesCountry } from "@redotech/redo-common-model/locale-codes";
import { RedoPlatformRpcClient } from "@redotech/redo-merchant-platform-rpc-client/client";
import { FeatureOnboardingType } from "@redotech/redo-onboarding-common/user-onboarding";
import { teamCanUseAi } from "@redotech/redo-team-common/team";
import { TeamContextValue } from "@redotech/redo-web/context/team-context";
import { isWmsRoute } from "../app/wms-route";
import { isRoleStepRequired } from "../welcome/welcome-flow";
import { isCommerceAgentLaunchOpen } from "./commerce-agent-launch-window";
import { isBeforeDhlFreeLabelsAnnouncementCutoff } from "./dhl-free-labels-announcement-window";
import { isBeforeGivingDayCutoff } from "./giving-day-teaser-window";
import {
  getMonthlyOrderCount,
  getRecoverOrderOfferTier,
} from "./recover-order-offer-tier";
import { isBeforeSuppliesFreeLabelsAnnouncementCutoff } from "./supplies-free-labels-announcement-window";

type TeamType = NonNullable<TeamContextValue>;

export interface FeatureOnboardingDefinition {
  type: FeatureOnboardingType;
  isEligible: (
    team: TeamType,
    user: GetUser | undefined,
    platformRpcClient: RedoPlatformRpcClient | undefined,
    permissions: Set<UserPermissions> | undefined,
    pathname?: string,
    search?: string,
  ) => Promise<boolean>;
  priority: number;
  retries?: number;
}

export async function isEligibleForConversion(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    !team.settings.checkoutOptimization?.enabled && team.platform === "shopify"
  );
}

export async function isEligibleForPackageProtectionPlus(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    team.settings.crossSellPopoutEnabled === true &&
    !team.settings.packageProtection?.packageProtectionPlusEnabled
  );
}

export async function isEligibleForExpandedWarranty(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return (
    team.settings.warranties?.enabled === true &&
    !team.settings.extendedWarranties?.enabled
  );
}

export async function isEligibleForManagedReturns(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return team.settings.returns?.enabled === true;
}

export async function isEligibleForManagedReturnsTrial(
  user: Pick<GetUser, "managedUserRoles"> | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  if (
    !user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ||
    !platformRpcClient
  ) {
    return false;
  }

  try {
    const response = await platformRpcClient.getFeatureFlag({
      flagKey: ReturnsFlagKey.MANAGED_RETURNS_TRIAL_ONBOARDING,
    });
    return response.enabled;
  } catch {
    return false;
  }
}

export async function isEligibleForBulkReturnActions(
  team: { settings: { returns?: { enabled?: boolean } } },
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
  permissions: Set<UserPermissions> | undefined,
): Promise<boolean> {
  return (
    team.settings.returns?.enabled === true &&
    !!permissions?.has(ReturnsAndClaimsPermissions.VIEW_RETURN)
  );
}

export async function isEligibleForSupportToAiUpsell(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // Check if current route includes "home" or "support"
  const currentPath = window.location.pathname;
  const shouldShowOnRoute = currentPath.includes("support");

  if (!shouldShowOnRoute) {
    return false;
  }

  const isPerOrderBilling =
    team.settings.support?.billing?.base?.version ===
      SupportBaseBillingVersion.PER_ORDER ||
    team.settings.support?.billing?.ai?.version ===
      SupportAiBillingVersion.PER_ORDER;

  if (isPerOrderBilling) {
    return false;
  }

  const isFreeCustomer =
    team.settings.support?.billing?.base?.pricePerMonthInCents === 0 &&
    team.settings.support?.billing?.base?.version !==
      SupportBaseBillingVersion.BUCKETED_USAGE;

  const isAiBillingAccepted =
    team.settings.support?.billing?.ai?.billingAccepted;

  return (
    team.settings.support?.enabled === true &&
    isFreeCustomer &&
    !isAiBillingAccepted
  );
}

export async function isEligibleForMarketingTexasSmsUpdate(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return Boolean(
    team.settings.marketing?.enabled && team.settings.marketing.textsEnabled,
  );
}

export async function isEligibleForIntercomMoved(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // This was a one-time notification about the chat widget moving
  // Since it's historical, return false to prevent showing to new users
  return false;
}

export async function isEligibleForBlackFridayTips(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return !!team.settings.marketing?.enabled;
}

export async function isEligibleForShopperAiWrapped(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  // Only show until January 1, 2026
  const expirationDate = Temporal.PlainDate.from("2026-01-01");
  const today = Temporal.Now.plainDateISO();
  if (Temporal.PlainDate.compare(today, expirationDate) >= 0) {
    return false;
  }
  return teamCanUseAi(team);
}

export async function isEligibleForHomepageAnalyticsMoved(
  team: TeamType,
): Promise<boolean> {
  const isOnSummaryPage = window.location.pathname.includes("/summary");
  if (!isOnSummaryPage) {
    return false;
  }
  return Boolean(
    team.settings.marketing?.enabled ||
    team.settings.orderTracking?.enabled ||
    team.settings.support?.enabled,
  );
}

export async function isEligibleForReclaimAnnouncement(
  team: TeamType,
): Promise<boolean> {
  return !team.settings.disputes?.automateEvidenceEnabled;
}

export async function isEligibleForSupportV2NewLook(): Promise<boolean> {
  return false;
}

export async function isEligibleForFeedbackAnnouncement(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
  permissions: Set<UserPermissions> | undefined,
): Promise<boolean> {
  // Playwright sets navigator.webdriver; hide so the card does not steal E2E clicks.
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  return true;
}

export async function isEligibleForGivingDayTeaser(): Promise<boolean> {
  return isBeforeGivingDayCutoff(Temporal.Now.instant());
}

export async function isEligibleForShippingClaimsAutoFiling(
  team: TeamType,
  user: GetUser | undefined,
): Promise<boolean> {
  if (!user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN)) {
    return false;
  }
  if (!window.location.pathname.includes("shipping")) {
    return false;
  }
  if (team.settings.outboundLabels?.claimSubmission?.enabled) {
    return false;
  }
  if (!team.settings.outboundLabels?.newOmsEnabled) {
    return false;
  }
  return true;
}

export async function isEligibleForSuppliesProductAnnouncement(team: {
  settings: { supplies?: { enabled?: boolean } };
  address?: { country?: string };
}): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  if (isWmsRoute(window.location.pathname)) {
    return false;
  }
  if (
    team.address?.country &&
    !["US", "United States", "USA"].includes(team.address.country)
  ) {
    return false;
  }
  // Show to every merchant that hasn't explicitly enabled the Supplies
  // storefront (includes non-OMS teams and teams with the flag unset).
  return team.settings.supplies?.enabled !== true;
}

// cspell:words Macoma Kortni Jeane Joelle
const SUPPLIES_FREE_LABELS_EXCLUDED_MERCHANT_NAMES = new Set(
  [
    "80eighty",
    "Baseballism",
    "Batch",
    "BW Essentials",
    "Camp Snap",
    "Chi Battery Systems",
    "Chip Theory",
    "Christy Dawn",
    "Freckled Poppy",
    "Frownies",
    "Jax",
    "Jax (Jax Athletics)",
    "Jax Athletics",
    "Lone Star Adhesive",
    "Macoma",
    "Manly Bands",
    "Pupford",
    "Roolee",
    "Wolfpak",
    "Bonobo Baby",
    "Kortni Jeane",
    "Taylor Joelle",
    "Little Sky Stone",
    "Steel Horse",
    "The Magic Brand",
  ].map(normalizeMerchantNameForSuppliesFreeLabelsExclusion),
);

function normalizeMerchantNameForSuppliesFreeLabelsExclusion(name?: string) {
  return (
    name
      ?.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() ?? ""
  );
}

function isExcludedFromSuppliesFreeLabelsAnnouncement(team: {
  displayName?: string;
  name?: string;
}) {
  return [team.name, team.displayName]
    .map(normalizeMerchantNameForSuppliesFreeLabelsExclusion)
    .some((name) => SUPPLIES_FREE_LABELS_EXCLUDED_MERCHANT_NAMES.has(name));
}

export async function isEligibleForSuppliesFreeLabelsAnnouncement(
  team: {
    _id: { toString(): string };
    address?: { country?: string };
    displayName?: string;
    name?: string;
  },
  user?: Pick<GetUser, "email">,
): Promise<boolean> {
  if (!isBeforeSuppliesFreeLabelsAnnouncementCutoff(Temporal.Now.instant())) {
    return false;
  }

  if (isRedoEmployeeEmail(user?.email)) {
    return false;
  }

  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }

  const homeBasePath = `/stores/${String(team._id)}/home`;
  if (!window.location.pathname.startsWith(homeBasePath)) {
    return false;
  }

  if (isExcludedFromSuppliesFreeLabelsAnnouncement(team)) {
    return false;
  }

  return isUnitedStatesCountry(team.address?.country);
}

const DHL_FREE_LABELS_ANNOUNCEMENT_TEAM_IDS = new Set([
  "6939bcbdbaf1e85775e3b941",
  "68473922b5eb801cc43db6cf",
  "6938cf0d1c18e1f375c0739f",
  "65bc29da0404ff0007372bab",
  "686ff7dbbbd350fe657b0f13",
  "671283b9f6eb9bb635276f6a",
  "6890d8ea7c5f73cf0b601a54",
  "69f117f78820482d7f9801a3",
  "69af7dd97b90de6e67193577",
  "68c9aea5895e396af7ac5937",
  "68c2fc701fb3e9c9bc4663ec",
  "674fa2d5d10eb77cba98a901",
  "6939fcc6bbdeb75fce2eebdc",
  "694a55b4c45a79867f6d950e",
  "6814d9ffb50c8b56bd1bd80c",
  "69976e03393377d316de2509",
  "6798f55b065241837e921e44",
  "6924affa15cecfecd7b5e5fc",
  "68237a8d769796f591e5b2e4",
  "66216732510f5e000799cfc4",
  "68f7b9525c98a7962e4cafbf",
  "67c09ce2151b63cc7a87a22d",
  "66f4509e60239d968c4edc61",
  "655cf62572ab89000692b3d3",
  "6983c55aaaf725e202a5dc74",
  "68c33fe0c78812863dbf1053",
  "66bd0fd92c53067f90cd5e6f",
  "681d0bd12411dde47facff83",
  "669fd11f2bd3d1fb06af36bf",
  "69e678b140ea5809e4a7bea1",
  "668c3d36f3338ad5e9779807",
  "691e2e32c7791f0a9e8e63d6",
  "6a04f632d5969e643069cea4",
  "698cf5f0883cf439aedf6057",
  "6532c12021dd1300076cffa6",
  "667ee819aa138cce9a5a1bb9",
  "6712c8407714e21f919f1c2e",
  "686468bf07ac8c5cfe77575a",
  "679a824c9d85aa7ef6c37883",
  "6695940024113fe5185c65c1",
  "69a89b9e9a879990e4d1e698",
  "66fedc593ff63f0c7a42ebd1",
  "6a68a68824dde903556b25d1",
  "68b9c5e8b476aedc9e2d0a9b",
  "6504dd2835617a00070e27bf",
  "6553d5b632aff90006bdf7da",
  "68af5ca9e5a628fc40e6cddf",
  "660ae17c7c38c100075dbac0",
  "654d086fe85deb0007155e88",
  "64b8110aea16bc0007364d72",
  "66ed904f84d84329eec59045",
  "6684257f0fc3979b6ca638ee",
  "67a29cddf6d7b8c14b167e5b",
  "66ad38ca451c30ee25fb7b50",
  "69e7bca60250872ea4902dde",
  "6942f9ff75bc0bcd92c0d887",
  "682b7c8f901eee1e9918aa5b",
  "699caa16ebe8b66c09cb5e0d",
  "67852bdedd45b31b5a1e8889",
  "69f37e578afda682dd9a16f3",
  "68fbbe9cc4e26ef28aed3fab",
  "6669f8a9b2d0bfe3e755a94e",
  "69500322a36ce7f2eb0fd130",
  "65c40ca73acf450008898b67",
  "681e3c67fc2278ae96ea9a95",
  "69d548f8f50ecce78494ecd9",
  "682e42575db9e5de978a0792",
  "678941d1ee7ee055c16ddf4f",
  "68a4c957a26459516447c34b",
  "695f064e45137dda19fdafaa",
  "69f0dac9e44be7aa85e04555",
  "6883caf404d5100dafad3331",
  "64d675a56b98a90006687710",
  "69cd77159e6e318d40d69f2d",
  "699e28f8912a40a5b2543c9e",
  "6810caf2e3e4be888a629219",
  "68c99e9cb6ba87b4b87276db",
  "689b57b07009733f529e9d54",
  "68c1c1d8aa0d444ad010b0e4",
  "69827cb199859d027b63b961",
  "66a1048937e5bab8a986a129",
  "697120ea14e850490e5b8ca2",
  "64c84223d5e3c700074d73c5",
  "67db7b92f8bc4ddfc78f946e",
  "69522d51d67fa3d91c1fe392",
  "66e1d15e53ca06258523bdf1",
  "686c59e4cb5cc319fae46b88",
  "6553a3211191d30007154002",
  "69811a9e6d2aba9be753a4cc",
  "67a13aa85aa25b0495ee2e61",
  "671fc193ab555b4a0317587f",
  "666fc598901af654967845d0",
  "64dfde8b64583e00077c6515",
  "682391639115dd79710de325",
  "6532c1f1ae750b0007122fd8",
  "681137423cc3e811b6bfb542",
  "6920c37bea519082a7ecaa53",
  "68f6b9babd0aa08a9478a4a7",
  "687033e62920be8c4e476239",
  "671948bdbb975d85e7a36733",
  "6696e876729c5fe26f55cc4f",
  "6495e07a7e915923a10f571c",
  "68d331c977dc55ca58a81cd6",
  "6717f87680c55a103c21ea70",
  "671a4757c8a72458e093d826",
  "689a67d5f8907cd382f0497e",
  "688275cbddf9b5f657f196ae",
  "6a0b7486082fe697ac4cd7e9",
]);

export async function isEligibleForDhlFreeLabelsAnnouncement(
  team: { _id: { toString(): string } },
  user?: Pick<GetUser, "email">,
): Promise<boolean> {
  return (
    isBeforeDhlFreeLabelsAnnouncementCutoff(Temporal.Now.instant()) &&
    !isRedoEmployeeEmail(user?.email) &&
    DHL_FREE_LABELS_ANNOUNCEMENT_TEAM_IDS.has(team._id.toString())
  );
}

function isEligibleForRecoverAnnouncementBase(): boolean {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  return !isWmsRoute(window.location.pathname);
}

export async function isEligibleForRecoverSubscriberAnnouncement(team: {
  settings: { recover?: { enabled?: boolean } };
}): Promise<boolean> {
  if (!isEligibleForRecoverAnnouncementBase()) {
    return false;
  }
  return team.settings.recover?.enabled === true;
}

export async function isEligibleForRecoverNonSubscriberAnnouncement(team: {
  settings: { recover?: { enabled?: boolean } };
  metricsSnapshot?: {
    totalOrderVolume?: { last30Days?: number };
    onlineStoreOrderVolume?: { last30Days?: number };
  };
}): Promise<boolean> {
  if (!isEligibleForRecoverAnnouncementBase()) {
    return false;
  }
  if (team.settings.recover?.enabled === true) {
    return false;
  }
  return getRecoverOrderOfferTier(getMonthlyOrderCount(team)) === "standard";
}

export async function isEligibleForRecoverNonSubscriberOffer2500(team: {
  settings: { recover?: { enabled?: boolean } };
  metricsSnapshot?: {
    totalOrderVolume?: { last30Days?: number };
    onlineStoreOrderVolume?: { last30Days?: number };
  };
}): Promise<boolean> {
  if (!isEligibleForRecoverAnnouncementBase()) {
    return false;
  }
  if (team.settings.recover?.enabled === true) {
    return false;
  }
  return getRecoverOrderOfferTier(getMonthlyOrderCount(team)) === "offer-2500";
}

export async function isEligibleForRecoverNonSubscriberOffer5000(team: {
  settings: { recover?: { enabled?: boolean } };
  metricsSnapshot?: {
    totalOrderVolume?: { last30Days?: number };
    onlineStoreOrderVolume?: { last30Days?: number };
  };
}): Promise<boolean> {
  if (!isEligibleForRecoverAnnouncementBase()) {
    return false;
  }
  if (team.settings.recover?.enabled === true) {
    return false;
  }
  return getRecoverOrderOfferTier(getMonthlyOrderCount(team)) === "offer-5000";
}

export async function isEligibleForCommerceAgentLaunch(): Promise<boolean> {
  if (!isCommerceAgentLaunchOpen(Temporal.Now.instant())) {
    return false;
  }
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  if (isWmsRoute(window.location.pathname)) {
    return false;
  }
  return true;
}

export async function isEligibleForMcpAnnouncement(
  user: Pick<GetUser, "jobProfile"> | undefined,
): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  if (isWmsRoute(window.location.pathname)) {
    return false;
  }
  // The required job/level backfill prompt owns the screen until the user has
  // answered it; wait for a session where it no longer shows.
  return !!user && !isRoleStepRequired(user.jobProfile);
}

export async function isEligibleForSignupFormsReplacedByPopups(
  team: TeamType,
  user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  return !!team.settings.marketing?.enabled;
}

export async function isEligibleForWithdrawalsOnboarding(
  team: TeamType,
  user: GetUser | undefined,
): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.webdriver) {
    return false;
  }
  return Boolean(user?.email) && !isRedoEmployeeEmail(user?.email);
}

export async function isEligibleForRedo3plInterestSurvey(
  team: TeamType,
  _user: GetUser | undefined,
  platformRpcClient: RedoPlatformRpcClient | undefined,
): Promise<boolean> {
  if (isWmsRoute(window.location.pathname)) {
    return false;
  }

  // Already-3PL teams should not see a 3PL interest survey.
  if (team.teamType === AccountTeamType.FulfillmentService) {
    return false;
  }

  // Brands connected to a 3PL should not see this survey either.
  if (team.fulfillmentServices.length > 0) {
    return false;
  }

  if (!platformRpcClient) {
    return false;
  }

  try {
    const response = await platformRpcClient.getUserFeatureFlag({
      flagKey: OmsFlagKey.REDO_3PL_INTEREST_SURVEY,
    });
    return response.enabled;
  } catch {
    return false;
  }
}

/**
 * Users will get exposed to the onboarding retries + 1 times. (if it isn't completed)
 */
export const featureOnboardingDefinitions: Record<
  Exclude<FeatureOnboardingType, FeatureOnboardingType.DISABLED>,
  FeatureOnboardingDefinition
> = {
  // Historical onboarding types - no longer actively shown
  [FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED]: {
    type: FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.HELP_ICON_MOVING]: {
    type: FeatureOnboardingType.HELP_ICON_MOVING,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.AGENTIC_CATALOG]: {
    type: FeatureOnboardingType.AGENTIC_CATALOG,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.INTERCOM_MOVED]: {
    type: FeatureOnboardingType.INTERCOM_MOVED,
    isEligible: isEligibleForIntercomMoved,
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.CONVERSION]: {
    type: FeatureOnboardingType.CONVERSION,
    isEligible: () => Promise.resolve(false),
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.MANAGED_RETURNS]: {
    type: FeatureOnboardingType.MANAGED_RETURNS,
    isEligible: isEligibleForManagedReturns,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.MANAGED_RETURNS_TRIAL]: {
    type: FeatureOnboardingType.MANAGED_RETURNS_TRIAL,
    isEligible: () => Promise.resolve(false),
    priority: 1,
    retries: 1,
  },
  [FeatureOnboardingType.PACKAGE_PROTECTION_PLUS]: {
    type: FeatureOnboardingType.PACKAGE_PROTECTION_PLUS,
    isEligible: () => Promise.resolve(false),
    priority: 4,
    retries: 1,
  },
  [FeatureOnboardingType.EXPANDED_WARRANTY]: {
    type: FeatureOnboardingType.EXPANDED_WARRANTY,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SUPPORT_TO_AI_UPSELL]: {
    type: FeatureOnboardingType.SUPPORT_TO_AI_UPSELL,
    isEligible: () => Promise.resolve(false),
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.COMMENTSOLD_REDO]: {
    type: FeatureOnboardingType.COMMENTSOLD_REDO,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SHOPPER_AI_WRAPPED]: {
    type: FeatureOnboardingType.SHOPPER_AI_WRAPPED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.DISPUTES_WELCOME]: {
    type: FeatureOnboardingType.DISPUTES_WELCOME,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.RECLAIM_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.RECLAIM_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED]: {
    type: FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.NAV_REORGANIZATION]: {
    type: FeatureOnboardingType.NAV_REORGANIZATION,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.SUPPORT_V2_NEW_LOOK]: {
    type: FeatureOnboardingType.SUPPORT_V2_NEW_LOOK,
    isEligible: isEligibleForSupportV2NewLook,
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.WITHDRAWALS_ONBOARDING]: {
    type: FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY]: {
    type: FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY,
    isEligible: () => Promise.resolve(false),
    priority: 2,
    retries: 0,
  },
  [FeatureOnboardingType.GIVING_DAY_TEASER]: {
    type: FeatureOnboardingType.GIVING_DAY_TEASER,
    isEligible: () => Promise.resolve(false),
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING]: {
    type: FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING,
    isEligible: () => Promise.resolve(false),
    priority: 3,
    retries: 2,
  },
  [FeatureOnboardingType.SUPPLIES_PRODUCT_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.SUPPLIES_PRODUCT_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 1,
    retries: 1,
  },
  [FeatureOnboardingType.SUPPLIES_FREE_LABELS_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.SUPPLIES_FREE_LABELS_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 1,
    retries: 1,
  },
  [FeatureOnboardingType.DHL_FREE_LABELS_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.DHL_FREE_LABELS_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.SIGNUP_FORMS_REPLACED_BY_POPUPS]: {
    type: FeatureOnboardingType.SIGNUP_FORMS_REPLACED_BY_POPUPS,
    isEligible: () => Promise.resolve(false),
    priority: 0,
    retries: 0,
  },
  [FeatureOnboardingType.BULK_RETURN_ACTIONS]: {
    type: FeatureOnboardingType.BULK_RETURN_ACTIONS,
    isEligible: isEligibleForBulkReturnActions,
    priority: 3,
    retries: 1,
  },
  [FeatureOnboardingType.RECOVER_SUBSCRIBER_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.RECOVER_SUBSCRIBER_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_ANNOUNCEMENT,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_2500]: {
    type: FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_2500,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_5000]: {
    type: FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_5000,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.COMMERCE_AGENT_LAUNCH]: {
    type: FeatureOnboardingType.COMMERCE_AGENT_LAUNCH,
    isEligible: () => isEligibleForCommerceAgentLaunch(),
    priority: -2,
    retries: 0,
  },
  [FeatureOnboardingType.MCP_ANNOUNCEMENT]: {
    type: FeatureOnboardingType.MCP_ANNOUNCEMENT,
    isEligible: (_team, user) => isEligibleForMcpAnnouncement(user),
    priority: -1,
    retries: 0,
  },
  // Historical onboarding types - no longer actively used but exist in MongoDB data
  [FeatureOnboardingType.CUSTOMS_INFORMATION_POPUP]: {
    type: FeatureOnboardingType.CUSTOMS_INFORMATION_POPUP,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.CROSS_BORDER_ONBOARDING]: {
    type: FeatureOnboardingType.CROSS_BORDER_ONBOARDING,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_VIEWS_MOVED]: {
    type: FeatureOnboardingType.OUTBOUND_VIEWS_MOVED,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL]: {
    type: FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.INTERNATIONAL_RETURNS]: {
    type: FeatureOnboardingType.INTERNATIONAL_RETURNS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE]: {
    type: FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION]: {
    type: FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.BLACK_FRIDAY_TIPS]: {
    type: FeatureOnboardingType.BLACK_FRIDAY_TIPS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.PERMISSIONS_ROLES]: {
    type: FeatureOnboardingType.PERMISSIONS_ROLES,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
  [FeatureOnboardingType.OUTBOUND_LABELS]: {
    type: FeatureOnboardingType.OUTBOUND_LABELS,
    isEligible: () => Promise.resolve(false),
    priority: 999,
    retries: 0,
  },
};
